<template>
  <div class="mobile-page">
    <div class="mobile-header g-header">
      <h2><el-icon><Upload /></el-icon> 上报信息</h2>
      <p>一线上报的隐患照片 / 视频与处理状态</p>
    </div>

    <div class="mobile-body">
      <div v-if="!gatherEnabled" class="white-card center-box">
        <el-icon :size="40" color="#c0c4cc"><Upload /></el-icon>
        <p class="sub-text">信息收集后台未配置（构建时设置 VITE_API_BASE）</p>
      </div>

      <template v-else>
        <GatherAuthCard v-if="showAuth" @authed="onAuthed" @skip="showAuth = false" />
        <template v-else>
        <div v-if="loading" class="center-box">
          <el-icon class="is-loading" :size="28"><Loading /></el-icon>
          <p class="sub-text">加载中…</p>
        </div>
        <div v-else-if="error" class="white-card center-box">
          <el-icon :size="36" color="#c5221f"><WarningFilled /></el-icon>
          <p class="sub-text">{{ error }}</p>
          <el-button size="small" type="primary" plain style="margin-top:10px" @click="loadStats">重试</el-button>
        </div>

        <template v-else>
          <div class="stat-grid">
            <div class="stat-card" @click="goRecords()">
              <div class="stat-num" style="color:#1a73e8">{{ stats.total }}</div>
              <div class="stat-label">上报记录总数</div>
            </div>
            <div class="stat-card" @click="goRecords({ status: '未处理' })">
              <div class="stat-num" style="color:#e6a23c">{{ stats.pending }}</div>
              <div class="stat-label">未处理</div>
            </div>
            <div class="stat-card" @click="goRecords({ status: '处理中' })">
              <div class="stat-num" style="color:#409eff">{{ stats.processing }}</div>
              <div class="stat-label">处理中</div>
            </div>
            <div class="stat-card" @click="goRecords({ status: '已处理' })">
              <div class="stat-num" style="color:#67c23a">{{ stats.done }}</div>
              <div class="stat-label">已处理</div>
            </div>
          </div>

          <div class="quick-row">
            <div class="quick-card" @click="goRecords({ today: 1 })">
              <el-icon :size="24" color="#e6a23c"><Sunny /></el-icon>
              <div>
                <div class="quick-num">{{ stats.today }}</div>
                <div class="quick-label">今日新增</div>
              </div>
            </div>
            <div class="quick-card" @click="goRecords()">
              <el-icon :size="24" color="#1a73e8"><List /></el-icon>
              <div>
                <div class="quick-num">{{ stats.total }}</div>
                <div class="quick-label">查看全部记录</div>
              </div>
            </div>
          </div>

          <div class="section-title"><span>最近上报</span><span class="link" @click="goRecords()">查看全部 ›</span></div>
          <div v-if="recent.length === 0" class="white-card center-box" style="padding:26px">
            <p class="sub-text" style="color:#9aa0a6">暂无记录</p>
          </div>
          <div v-else class="rec-list">
            <div class="g-item" v-for="r in recent" :key="r.id" @click="$router.push(`/gather-record/${r.id}`)">
              <div class="g-top">
                <span class="g-id mono">{{ r.id }}</span>
                <span class="pill" :class="statusPill(r.status)">{{ r.status }}</span>
              </div>
              <div class="g-desc">{{ r.description || '（无备注）' }}</div>
              <div class="g-media-tags" v-if="(r.images || []).length || (r.videos || []).length">
                <span v-if="(r.images || []).length" class="tag">📷 {{ r.images.length }} 张照片</span>
                <span v-if="(r.videos || []).length" class="tag">🎬 {{ r.videos.length }} 段视频</span>
              </div>
              <div class="g-meta">
                <span v-if="r.reporter">👤 {{ r.reporter }}</span>
                <span v-if="(r.images || []).length">📷 {{ r.images.length }}</span>
                <span v-if="(r.videos || []).length">🎬 {{ r.videos.length }}</span>
                <span class="g-time">{{ fmt(r.reportTime) }}</span>
              </div>
            </div>
          </div>
        </template>
      </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { fetchGatherRecords, gatherEnabled, isAuthError } from '../api'
import GatherAuthCard from '../components/GatherAuthCard.vue'
import dayjs from 'dayjs'

const router = useRouter()
const loading = ref(true)
const error = ref('')
const recent = ref([])
const stats = ref({ total: 0, pending: 0, processing: 0, done: 0, today: 0 })
const showAuth = ref(false)

function onAuthed() {
  showAuth.value = false
  loadStats()
}

function fmt(t) { return t ? dayjs(t).format('MM-DD HH:mm') : '' }
function statusPill(s) {
  if (s === '已处理') return 'pill-green'
  if (s === '处理中') return 'pill-orange'
  return 'pill-red'
}

function goRecords(query = {}) {
  router.push({ path: '/gather-records', query })
}

async function loadStats() {
  loading.value = true
  error.value = ''
  try {
    const pageSize = 100
    const [all, p1, p2, p3, today] = await Promise.all([
      fetchGatherRecords({ page: 1, pageSize }),
      fetchGatherRecords({ page: 1, pageSize, status: '未处理' }),
      fetchGatherRecords({ page: 1, pageSize, status: '处理中' }),
      fetchGatherRecords({ page: 1, pageSize, status: '已处理' }),
      fetchGatherRecords({ page: 1, pageSize, startDate: dayjs().format('YYYY-MM-DD'), endDate: dayjs().format('YYYY-MM-DD') })
    ])
    stats.value = {
      total: all.total || 0,
      pending: p1.total || 0,
      processing: p2.total || 0,
      done: p3.total || 0,
      today: today.total || 0
    }
    recent.value = (all.data || []).slice(0, 5)
  } catch (e) {
    if (isAuthError(e)) {
      showAuth.value = true
      error.value = ''
    } else {
      error.value = e.message || '加载失败'
    }
  } finally {
    loading.value = false
  }
}

onMounted(loadStats)
</script>

<style scoped>
.g-header { background: linear-gradient(135deg, #0b57d0, #4285f4); }

.quick-row { display: flex; gap: 10px; margin-top: 10px; }
.quick-card {
  flex: 1; background: #fff; border-radius: 14px; padding: 12px; display: flex;
  align-items: center; gap: 10px; cursor: pointer; box-shadow: 0 3px 10px rgba(31,45,61,.06);
}
.quick-card:active { transform: scale(.97); }
.quick-num { font-size: 18px; font-weight: 800; color: #303133; line-height: 1.2; }
.quick-label { font-size: 11px; color: #909399; }

.g-item { background: #fff; border-radius: 14px; padding: 12px 14px; margin-bottom: 10px; box-shadow: 0 3px 12px rgba(31,45,61,.06); cursor: pointer; }
.g-item:active { transform: scale(.99); }
.g-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.g-id { font-size: 12.5px; font-weight: 700; color: #5f6368; }
.g-desc { font-size: 14px; color: #202124; line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.g-media-tags { display: flex; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
.g-media-tags .tag { font-size: 11.5px; color: #5f6368; background: #f1f3f4; border-radius: 8px; padding: 2px 9px; }
.g-meta { display: flex; gap: 9px; font-size: 11.5px; color: #9aa0a6; margin-top: 8px; flex-wrap: wrap; }
.g-time { margin-left: auto; }
</style>
